import React, { useEffect, useState, useCallback } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useToast } from '../../Helpers/ToastHelper';
import { useTranslation } from 'react-i18next';
import { DataTable, Column, SCIcon, Dialog, ReactCountryFlag, ProgressBar } from '../../Common/Imports';
import { ApiResponse, ConfigService_EXT, Countries_EXT } from '../../Models/Classes';
import ConfigServiceNew from './ConfigService_New';

interface ConfigServiceListProps {
    stakeholderId: number;
}

export const ConfigServiceList: React.FC<ConfigServiceListProps> = ({ stakeholderId }) => {
    const AxiosHelper = useAxios();
    const { showToast } = useToast();
    const { t } = useTranslation();
    const [TableData, SetTableData] = useState<ConfigService_EXT[] | undefined>();
    const [TableLoader, SetTableLoader] = useState<boolean>(false);
    const [CreateNewData, setCreateNewData] = useState<boolean>(false);
    const [CountriesData, setCountriesData] = useState<Countries_EXT[] | undefined>();

    const GetData = useCallback(async () => {
        SetTableLoader(true);
        try {

            const resp = await AxiosHelper.get<ApiResponse<ConfigService_EXT[]>>(`StakeholderConfigs/Services/GetList/${stakeholderId}`);
            if (resp.data.code == 0) {
                SetTableData(resp.data.data);
            }
            const resp2 = await AxiosHelper.get<ApiResponse<Countries_EXT[]>>(`Countries/GetList`);
            if (resp2.data.code === 0) {
                setCountriesData(resp2.data.data);
            }
        } catch {
            showToast('error', 'Error', t("Common.Toasts.New.Error"));
        } finally {
            SetTableLoader(false);
        }
    }, [AxiosHelper, showToast, stakeholderId, t]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const deleteRecord = async (data: ConfigService_EXT) => {
        SetTableLoader(true);
        try {

            const resp = await AxiosHelper.post<ApiResponse<ConfigService_EXT[]>>(`StakeholderConfigs/Services/Delete`, data);
            if (resp.data.code == 0) {
                showToast('success', 'Success', t("Common.Toasts.Delete.Success"));
                GetData();
            }
        } catch {
            showToast('error', 'Error', t("Common.Toasts.New.Error"));
        } finally {
            SetTableLoader(false);
        }
    }

    const deleteBody = (data: ConfigService_EXT) => {
        return <SCIcon Name="Delete" Classes="text-danger" Pointer OnClick={() => deleteRecord(data)} />;
    }

    return (
        <>
            {
                CreateNewData &&
                <Dialog
                    header="New Service Configuration"
                    visible={CreateNewData ? true : false}
                    onHide={() => { setCreateNewData(false); GetData() }}>
                    <ConfigServiceNew stakeholderId={stakeholderId} OnSave={() => { setCreateNewData(false); GetData() }} />
                </Dialog>
            }
            <div className="col d-flex justify-content-end">
                <SCIcon Name="Add" Classes="text-primary" Pointer OnClick={() => setCreateNewData(true)}></SCIcon>
            </div>
            {
                TableLoader ?
                    <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
                    :
                    <DataTable
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 20, 50]}
                        value={TableData}
                        size="small"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        stripedRows>
                        <Column field="idCountry"
                            header={t("Stakeholder.Configs_Service.Country")}
                            body={(e: ConfigService_EXT) => {
                                const country = CountriesData?.find(m => m.idCountry === e.idCountry);
                                return <><ReactCountryFlag
                                    countryCode={country?.isO2 ?? ""}
                                    svg
                                    title="" // Ensure title matches the data label
                                    style={{ marginRight: "10px" }} />
                                    {country?.name || ""}</>;
                            }}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            sortable
                            field="sendTentativeNumber"
                            header={t("Stakeholder.Configs_Service.TentativeNumber")}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            sortable
                            field="hoursBefore"
                            header={t("Stakeholder.Configs_Service.HoursBefore")}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            sortable
                            field="idType"
                            header={t("Stakeholder.Configs_Service.Type")}
                            bodyClassName="table-cell-nowrap"
                            body={(e: ConfigService_EXT) => {
                                return e.idType === 5 ? "Check-in Date" : "Cancellation Date";
                            }} />
                        <Column
                            sortable
                            field="dayOfWeek"
                            header={t("Stakeholder.Configs_Service.DayOfWeek")}
                            bodyClassName="table-cell-nowrap"
                            body={(e: ConfigService_EXT) => {
                                return t("Common.DaysOfWeek." + e.dayOfWeek);
                            }} />
                        <Column
                            body={(e: ConfigService_EXT) => deleteBody(e)}
                            style={{ maxWidth: '50px!important', width: "50px" }}
                            bodyClassName="table-cell-nowrap" />
                    </DataTable>
            }
        </>
    );
};

export default ConfigServiceList;
